import { Injectable, inject } from '@angular/core';
import { Observable, of, map, tap } from 'rxjs';

import { SUPPORT_RAIL_KEY } from './ciam-auth.service';
import { SupportApiService } from '../core/support-api.service';
import { CertificationOverview } from '../core/models';

type CertEligibility = CertificationOverview['eligibility'];

/**
 * Client-side view of the signed-in principal's ROLE and (for VARs) certification
 * standing — the inputs to nav visibility and the route guards. The rail chosen at
 * login (`bt_support_rail`) is the active-principal indicator, the SAME notion the
 * interceptor uses to pick which token to attach, so gating on it keeps UI and token
 * consistent. The SERVER remains the real authority (admin/ticket routes are role- and
 * grant-gated regardless of what this shows); this only prevents a VAR from being SHOWN
 * staff surfaces and forces an uncertified VAR into the certification flow.
 *
 * Certification eligibility is fetched once from `GET /me/certification` and cached.
 * Only ever fetched for a VAR — staff would 403 on that route — so callers gate on
 * isStaff()/isVar() before ensureCertStatus().
 */
@Injectable({ providedIn: 'root' })
export class VarAccessService {
  private api = inject(SupportApiService);
  private _eligibility: CertEligibility | null = null;

  /** The active session authenticated on the partner (CIAM) rail. */
  isVar(): boolean {
    return sessionStorage.getItem(SUPPORT_RAIL_KEY) === 'partner';
  }

  /** Staff = anything that is not the partner rail (workforce, or no rail). */
  isStaff(): boolean {
    return !this.isVar();
  }

  /** True once cert status has been loaded this session (nav reads it synchronously). */
  certLoaded(): boolean {
    return this._eligibility !== null;
  }

  /** Certified = the VAR may access the inbox. False when unloaded or not yet certified. */
  certified(): boolean {
    return this._eligibility === 'certified';
  }

  /**
   * Resolve the VAR's certification eligibility, caching it. Returns the cached value
   * immediately once loaded. VAR-only — do not call for staff (they 403 on the route).
   */
  ensureCertStatus(): Observable<CertEligibility> {
    if (this._eligibility !== null) {
      return of(this._eligibility);
    }
    return this.api.getCertificationOverview().pipe(
      tap((s) => { this._eligibility = s.eligibility; }),
      map((s) => s.eligibility),
    );
  }

  /** Drop the cache so the next ensureCertStatus() re-fetches — call after an exam
   * grants certification, so the inbox unlocks without a full reload. */
  refresh(): void {
    this._eligibility = null;
  }
}
